// li-funnel-redesign v1 — the declarative step table for the editorial funnel.
//
// The questions, their order, their translation keys and the values each answer
// submits are copied verbatim from the promoted baseline
// (`src/pages/baseline/onboarding/`). Only the presentation changes, so the
// experiment measures layout and pacing rather than a different questionnaire.
import { addAgeToLocalStorage } from '@app-studio-web/ui/helpers/localStorage'
import { type AnswerType } from '@app-studio-web/ui/types'

const FUNNEL_SECTIONS = [
  'aboutYou',
  'yourGoal',
  'yourObstacles',
  'yourPlan',
] as const

type FunnelSection = (typeof FUNNEL_SECTIONS)[number]

/**
 * Sections the header rail renders. Sign-up follows the questions in the same
 * flow, so it owns the last segment even though no funnel step maps to it.
 */
export const FUNNEL_PROGRESS_SECTIONS = [
  ...FUNNEL_SECTIONS,
  'yourAccount',
] as const

interface BaseStep {
  path: string
  section: FunnelSection
}

export type FunnelStep = BaseStep &
  (
    | {
        answers: AnswerType[]
        onSelect?: (value: string) => void
        subtitleKey?: string
        titleKey: string
        type: 'single'
        withLogin?: boolean
      }
    | {
        answers: AnswerType[]
        subtitleKey: string
        titleKey: string
        type: 'multi'
      }
    | { emoji: string; titleKey: string; type: 'statement' }
    | { durationMs: number; titleKey: string; type: 'loader' }
  )

const languageAnswers: AnswerType[] = [
  { emoji: '🇬🇧', title: 'pages.onboarding.languages.en', value: 'en' },
  { emoji: '🇪🇸', title: 'pages.onboarding.languages.es', value: 'es' },
  { emoji: '🇩🇪', title: 'pages.onboarding.languages.de', value: 'de' },
  { emoji: '🇫🇷', title: 'pages.onboarding.languages.fr', value: 'fr' },
  { emoji: '🇮🇹', title: 'pages.onboarding.languages.it', value: 'it' },
  { emoji: '🇵🇹', title: 'pages.onboarding.languages.pt', value: 'pt' },
  { emoji: '🇳🇱', title: 'pages.onboarding.languages.nl', value: 'nl' },
  { emoji: '🇯🇵', title: 'pages.onboarding.languages.ja', value: 'ja' },
  { emoji: '🇨🇳', title: 'pages.onboarding.languages.ch', value: 'zh' },
  { emoji: '🇰🇷', title: 'pages.onboarding.languages.ko', value: 'ko' },
  { emoji: '🇷🇺', title: 'pages.onboarding.languages.ru', value: 'ru' },
  { emoji: '🇮🇩', title: 'pages.onboarding.languages.in', value: 'id' },
  { emoji: '🇵🇱', title: 'pages.onboarding.languages.pl', value: 'pl' },
  { emoji: '🇸🇪', title: 'pages.onboarding.languages.sv', value: 'sv' },
  { emoji: '🇹🇷', title: 'pages.onboarding.languages.tr', value: 'tr' },
]

const ageAnswers: AnswerType[] = [
  {
    emoji: '🧑',
    title: 'pages.onboarding.ageQuestion.answer1',
    value: '18_24',
  },
  {
    emoji: '🧑‍💼',
    title: 'pages.onboarding.ageQuestion.answer2',
    value: '25_34',
  },
  {
    emoji: '👨',
    title: 'pages.onboarding.ageQuestion.answer3',
    value: '35_44',
  },
  {
    emoji: '🧓',
    title: 'pages.onboarding.ageQuestion.answer4',
    value: '45_54',
  },
  {
    emoji: '👴',
    title: 'pages.onboarding.ageQuestion.answer5',
    value: '55_64',
  },
  {
    emoji: '🎖️',
    title: 'pages.onboarding.ageQuestion.answer6',
    value: '65_plus',
  },
  {
    emoji: '💫',
    title: 'pages.onboarding.ageQuestion.answer7',
    value: 'other',
  },
]

const levelAnswers: AnswerType[] = [
  {
    emoji: '🐣',
    subtitle: 'pages.onboarding.levelQuestion.answer1Subtitle',
    title: 'pages.onboarding.levelQuestion.answer1',
    value: 'beginner',
  },
  {
    emoji: '🌱',
    subtitle: 'pages.onboarding.levelQuestion.answer2Subtitle',
    title: 'pages.onboarding.levelQuestion.answer2',
    value: 'elementary',
  },
  {
    emoji: '💬',
    subtitle: 'pages.onboarding.levelQuestion.answer3Subtitle',
    title: 'pages.onboarding.levelQuestion.answer3',
    value: 'intermediate',
  },
  {
    emoji: '🚀',
    subtitle: 'pages.onboarding.levelQuestion.answer4Subtitle',
    title: 'pages.onboarding.levelQuestion.answer4',
    value: 'advanced',
  },
]

const learningContextAnswers: AnswerType[] = [
  {
    emoji: '✈️',
    subtitle: 'pages.onboarding.learningContext.answer1Subtitle',
    title: 'pages.onboarding.learningContext.answer1',
  },
  {
    emoji: '💼',
    subtitle: 'pages.onboarding.learningContext.answer2Subtitle',
    title: 'pages.onboarding.learningContext.answer2',
  },
  {
    emoji: '❤️',
    subtitle: 'pages.onboarding.learningContext.answer3Subtitle',
    title: 'pages.onboarding.learningContext.answer3',
  },
  {
    emoji: '🎓',
    subtitle: 'pages.onboarding.learningContext.answer4Subtitle',
    title: 'pages.onboarding.learningContext.answer4',
  },
  {
    emoji: '🌍',
    subtitle: 'pages.onboarding.learningContext.answer5Subtitle',
    title: 'pages.onboarding.learningContext.answer5',
  },
  {
    emoji: '🏠',
    subtitle: 'pages.onboarding.learningContext.answer6Subtitle',
    title: 'pages.onboarding.learningContext.answer6',
  },
]

const whyAnswers: AnswerType[] = [
  { emoji: '✈️', title: 'pages.onboarding.whyQuestion.answer1' },
  { emoji: '💼', title: 'pages.onboarding.whyQuestion.answer2' },
  { emoji: '🏠', title: 'pages.onboarding.whyQuestion.answer3' },
  { emoji: '📚', title: 'pages.onboarding.whyQuestion.answer4' },
  { emoji: '🎭', title: 'pages.onboarding.whyQuestion.answer5' },
  { emoji: '🤔', title: 'pages.onboarding.whyQuestion.answer6' },
]

const deeperMotivationAnswers: AnswerType[] = [
  {
    emoji: '🦋',
    subtitle: 'pages.onboarding.deeperMotivation.answer1Subtitle',
    title: 'pages.onboarding.deeperMotivation.answer1',
  },
  {
    emoji: '🤝',
    subtitle: 'pages.onboarding.deeperMotivation.answer2Subtitle',
    title: 'pages.onboarding.deeperMotivation.answer2',
  },
  {
    emoji: '🏆',
    subtitle: 'pages.onboarding.deeperMotivation.answer3Subtitle',
    title: 'pages.onboarding.deeperMotivation.answer3',
  },
  {
    emoji: '🧠',
    subtitle: 'pages.onboarding.deeperMotivation.answer4Subtitle',
    title: 'pages.onboarding.deeperMotivation.answer4',
  },
  {
    emoji: '🌱',
    subtitle: 'pages.onboarding.deeperMotivation.answer5Subtitle',
    title: 'pages.onboarding.deeperMotivation.answer5',
  },
  {
    emoji: '💫',
    subtitle: 'pages.onboarding.deeperMotivation.answer6Subtitle',
    title: 'pages.onboarding.deeperMotivation.answer6',
  },
]

const goalsAnswers: AnswerType[] = [
  { emoji: '💬', title: 'pages.onboarding.goalsQuestion.answer1' },
  { emoji: '🗣️', title: 'pages.onboarding.goalsQuestion.answer2' },
  { emoji: '📚', title: 'pages.onboarding.goalsQuestion.answer3' },
  { emoji: '🎬', title: 'pages.onboarding.goalsQuestion.answer4' },
  { emoji: '💼', title: 'pages.onboarding.goalsQuestion.answer5' },
  { emoji: '📝', title: 'pages.onboarding.goalsQuestion.answer6' },
]

const goalsTimelineAnswers: AnswerType[] = [
  {
    emoji: '⚡',
    subtitle: 'pages.onboarding.goalsTimeline.answer1Subtitle',
    title: 'pages.onboarding.goalsTimeline.answer1',
  },
  {
    emoji: '📅',
    subtitle: 'pages.onboarding.goalsTimeline.answer2Subtitle',
    title: 'pages.onboarding.goalsTimeline.answer2',
  },
  {
    emoji: '🗓️',
    subtitle: 'pages.onboarding.goalsTimeline.answer3Subtitle',
    title: 'pages.onboarding.goalsTimeline.answer3',
  },
  {
    emoji: '📆',
    subtitle: 'pages.onboarding.goalsTimeline.answer4Subtitle',
    title: 'pages.onboarding.goalsTimeline.answer4',
  },
  {
    emoji: '🌿',
    subtitle: 'pages.onboarding.goalsTimeline.answer5Subtitle',
    title: 'pages.onboarding.goalsTimeline.answer5',
  },
]

const firstMilestoneAnswers: AnswerType[] = [
  {
    emoji: '👋',
    subtitle: 'pages.onboarding.firstMilestone.answer1Subtitle',
    title: 'pages.onboarding.firstMilestone.answer1',
  },
  {
    emoji: '🛒',
    subtitle: 'pages.onboarding.firstMilestone.answer2Subtitle',
    title: 'pages.onboarding.firstMilestone.answer2',
  },
  {
    emoji: '💬',
    subtitle: 'pages.onboarding.firstMilestone.answer3Subtitle',
    title: 'pages.onboarding.firstMilestone.answer3',
  },
  {
    emoji: '📺',
    subtitle: 'pages.onboarding.firstMilestone.answer4Subtitle',
    title: 'pages.onboarding.firstMilestone.answer4',
  },
  {
    emoji: '✍️',
    subtitle: 'pages.onboarding.firstMilestone.answer5Subtitle',
    title: 'pages.onboarding.firstMilestone.answer5',
  },
  {
    emoji: '🎯',
    subtitle: 'pages.onboarding.firstMilestone.answer6Subtitle',
    title: 'pages.onboarding.firstMilestone.answer6',
  },
]

const timeAnswers: AnswerType[] = [
  {
    emoji: '⚡️',
    subtitle: 'pages.onboarding.timeQuestion.answer1Subtitle',
    title: 'pages.onboarding.timeQuestion.answer1',
  },
  {
    emoji: '🎯️',
    subtitle: 'pages.onboarding.timeQuestion.answer2Subtitle',
    title: 'pages.onboarding.timeQuestion.answer2',
  },
  {
    emoji: '📈',
    subtitle: 'pages.onboarding.timeQuestion.answer3Subtitle',
    title: 'pages.onboarding.timeQuestion.answer3',
  },
  {
    emoji: '🚀',
    subtitle: 'pages.onboarding.timeQuestion.answer4Subtitle',
    title: 'pages.onboarding.timeQuestion.answer4',
  },
  {
    emoji: '💪',
    subtitle: 'pages.onboarding.timeQuestion.answer5Subtitle',
    title: 'pages.onboarding.timeQuestion.answer5',
  },
]

const mistakesAnswers: AnswerType[] = [
  {
    emoji: '😅️',
    subtitle: 'pages.onboarding.mistakesQuestion.answer1Subtitle',
    title: 'pages.onboarding.mistakesQuestion.answer1',
  },
  {
    emoji: '🙂',
    subtitle: 'pages.onboarding.mistakesQuestion.answer2Subtitle',
    title: 'pages.onboarding.mistakesQuestion.answer2',
  },
  {
    emoji: '😎',
    subtitle: 'pages.onboarding.mistakesQuestion.answer3Subtitle',
    title: 'pages.onboarding.mistakesQuestion.answer3',
  },
  {
    emoji: '🔥',
    subtitle: 'pages.onboarding.mistakesQuestion.answer4Subtitle',
    title: 'pages.onboarding.mistakesQuestion.answer4',
  },
]

const selfEfficacyAnswers: AnswerType[] = [
  {
    emoji: '🚀',
    subtitle: 'pages.onboarding.selfEfficacy.answer1Subtitle',
    title: 'pages.onboarding.selfEfficacy.answer1',
  },
  {
    emoji: '😊',
    subtitle: 'pages.onboarding.selfEfficacy.answer2Subtitle',
    title: 'pages.onboarding.selfEfficacy.answer2',
  },
  {
    emoji: '🤔',
    subtitle: 'pages.onboarding.selfEfficacy.answer3Subtitle',
    title: 'pages.onboarding.selfEfficacy.answer3',
  },
  {
    emoji: '😟',
    subtitle: 'pages.onboarding.selfEfficacy.answer4Subtitle',
    title: 'pages.onboarding.selfEfficacy.answer4',
  },
  {
    emoji: '🌱',
    subtitle: 'pages.onboarding.selfEfficacy.answer5Subtitle',
    title: 'pages.onboarding.selfEfficacy.answer5',
  },
]

const topicsAnswers: AnswerType[] = [
  { emoji: '🍳', title: 'pages.onboarding.topicsQuestion.answer1' },
  { emoji: '✈️', title: 'pages.onboarding.topicsQuestion.answer2' },
  { emoji: '💼', title: 'pages.onboarding.topicsQuestion.answer3' },
  { emoji: '💻', title: 'pages.onboarding.topicsQuestion.answer4' },
  { emoji: '⚽', title: 'pages.onboarding.topicsQuestion.answer5' },
  { emoji: '🎨', title: 'pages.onboarding.topicsQuestion.answer6' },
  { emoji: '🔬', title: 'pages.onboarding.topicsQuestion.answer7' },
  { emoji: '👗', title: 'pages.onboarding.topicsQuestion.answer8' },
  { emoji: '🧘', title: 'pages.onboarding.topicsQuestion.answer9' },
  { emoji: '🤔', title: 'pages.onboarding.topicsQuestion.answer10' },
]

const cognitiveStyleAnswers: AnswerType[] = [
  {
    emoji: '🔍',
    subtitle: 'pages.onboarding.cognitiveStyle.answer1Subtitle',
    title: 'pages.onboarding.cognitiveStyle.answer1',
  },
  {
    emoji: '🎯',
    subtitle: 'pages.onboarding.cognitiveStyle.answer2Subtitle',
    title: 'pages.onboarding.cognitiveStyle.answer2',
  },
  {
    emoji: '🧩',
    subtitle: 'pages.onboarding.cognitiveStyle.answer3Subtitle',
    title: 'pages.onboarding.cognitiveStyle.answer3',
  },
  {
    emoji: '🔄',
    subtitle: 'pages.onboarding.cognitiveStyle.answer4Subtitle',
    title: 'pages.onboarding.cognitiveStyle.answer4',
  },
]

const pastMethodsAnswers: AnswerType[] = [
  { emoji: '📱', title: 'pages.onboarding.pastMethods.answer1' },
  { emoji: '📚', title: 'pages.onboarding.pastMethods.answer2' },
  { emoji: '🎧', title: 'pages.onboarding.pastMethods.answer3' },
  { emoji: '👨‍🏫', title: 'pages.onboarding.pastMethods.answer4' },
  { emoji: '🏫', title: 'pages.onboarding.pastMethods.answer5' },
  { emoji: '📺', title: 'pages.onboarding.pastMethods.answer6' },
  { emoji: '🌐', title: 'pages.onboarding.pastMethods.answer7' },
  { emoji: '✨', title: 'pages.onboarding.pastMethods.answer8' },
]

const biggestFrustrationAnswers: AnswerType[] = [
  {
    emoji: '😴',
    subtitle: 'pages.onboarding.biggestFrustration.answer1Subtitle',
    title: 'pages.onboarding.biggestFrustration.answer1',
  },
  {
    emoji: '🤐',
    subtitle: 'pages.onboarding.biggestFrustration.answer2Subtitle',
    title: 'pages.onboarding.biggestFrustration.answer2',
  },
  {
    emoji: '⏰',
    subtitle: 'pages.onboarding.biggestFrustration.answer3Subtitle',
    title: 'pages.onboarding.biggestFrustration.answer3',
  },
  {
    emoji: '📉',
    subtitle: 'pages.onboarding.biggestFrustration.answer4Subtitle',
    title: 'pages.onboarding.biggestFrustration.answer4',
  },
  {
    emoji: '😤',
    subtitle: 'pages.onboarding.biggestFrustration.answer5Subtitle',
    title: 'pages.onboarding.biggestFrustration.answer5',
  },
  {
    emoji: '🌟',
    subtitle: 'pages.onboarding.biggestFrustration.answer6Subtitle',
    title: 'pages.onboarding.biggestFrustration.answer6',
  },
]

const picturingAnswers: AnswerType[] = [
  { emoji: '🍽️', title: 'pages.onboarding.picturingQuestion.answer1' },
  { emoji: '🤝', title: 'pages.onboarding.picturingQuestion.answer2' },
  { emoji: '💼', title: 'pages.onboarding.picturingQuestion.answer3' },
  { emoji: '🏠', title: 'pages.onboarding.picturingQuestion.answer4' },
  { emoji: '❤️', title: 'pages.onboarding.picturingQuestion.answer5' },
  { emoji: '🎬', title: 'pages.onboarding.picturingQuestion.answer6' },
]

const identityShiftAnswers: AnswerType[] = [
  {
    emoji: '✨',
    subtitle: 'pages.onboarding.identityShift.answer1Subtitle',
    title: 'pages.onboarding.identityShift.answer1',
  },
  {
    emoji: '🌅',
    subtitle: 'pages.onboarding.identityShift.answer2Subtitle',
    title: 'pages.onboarding.identityShift.answer2',
  },
  {
    emoji: '🤞',
    subtitle: 'pages.onboarding.identityShift.answer3Subtitle',
    title: 'pages.onboarding.identityShift.answer3',
  },
  {
    emoji: '🌱',
    subtitle: 'pages.onboarding.identityShift.answer4Subtitle',
    title: 'pages.onboarding.identityShift.answer4',
  },
]

const finalCommitmentAnswers: AnswerType[] = [
  {
    emoji: '🚀',
    subtitle: 'pages.onboarding.finalCommitment.answer1Subtitle',
    title: 'pages.onboarding.finalCommitment.answer1',
  },
  {
    emoji: '💪',
    subtitle: 'pages.onboarding.finalCommitment.answer2Subtitle',
    title: 'pages.onboarding.finalCommitment.answer2',
  },
  {
    emoji: '🌱',
    subtitle: 'pages.onboarding.finalCommitment.answer3Subtitle',
    title: 'pages.onboarding.finalCommitment.answer3',
  },
]

const subscriptionCountAnswers: AnswerType[] = [
  {
    emoji: '📋',
    subtitle: 'pages.onboarding.subscriptionCount.answer1Subtitle',
    title: 'pages.onboarding.subscriptionCount.answer1',
    value: 'oneToTwo',
  },
  {
    emoji: '📦',
    subtitle: 'pages.onboarding.subscriptionCount.answer2Subtitle',
    title: 'pages.onboarding.subscriptionCount.answer2',
    value: 'threeToFive',
  },
  {
    emoji: '🏆',
    subtitle: 'pages.onboarding.subscriptionCount.answer3Subtitle',
    title: 'pages.onboarding.subscriptionCount.answer3',
    value: 'fivePlus',
  },
  {
    emoji: '🆓',
    subtitle: 'pages.onboarding.subscriptionCount.answer4Subtitle',
    title: 'pages.onboarding.subscriptionCount.answer4',
    value: 'none',
  },
]

export const FUNNEL_STEPS: FunnelStep[] = [
  {
    answers: languageAnswers,
    onSelect: (value) => localStorage.setItem('selectedLanguage', value),
    path: 'question-language',
    section: 'aboutYou',
    titleKey: 'pages.onboarding.languageQuestion.title',
    type: 'single',
    withLogin: true,
  },
  {
    answers: ageAnswers,
    onSelect: addAgeToLocalStorage,
    path: 'age',
    section: 'aboutYou',
    subtitleKey: 'pages.onboarding.ageQuestion.subtitle',
    titleKey: 'pages.onboarding.ageQuestion.title',
    type: 'single',
  },
  {
    answers: levelAnswers,
    onSelect: (value) => localStorage.setItem('comfortLevel', value),
    path: 'level-question',
    section: 'aboutYou',
    subtitleKey: 'pages.onboarding.levelQuestion.subtitle',
    titleKey: 'pages.onboarding.levelQuestion.title',
    type: 'single',
  },
  {
    answers: learningContextAnswers,
    path: 'learning-context',
    section: 'aboutYou',
    subtitleKey: 'pages.onboarding.learningContext.subtitle',
    titleKey: 'pages.onboarding.learningContext.title',
    type: 'single',
  },
  {
    answers: whyAnswers,
    path: 'why-question',
    section: 'yourGoal',
    titleKey: 'pages.onboarding.whyQuestion.title',
    type: 'single',
  },
  {
    emoji: '🎯',
    path: 'why-feedback',
    section: 'yourGoal',
    titleKey: 'pages.onboarding.whyFeedback.title',
    type: 'statement',
  },
  {
    answers: deeperMotivationAnswers,
    path: 'deeper-motivation',
    section: 'yourGoal',
    subtitleKey: 'pages.onboarding.deeperMotivation.subtitle',
    titleKey: 'pages.onboarding.deeperMotivation.title',
    type: 'single',
  },
  {
    emoji: '✨',
    path: 'dream-scenario',
    section: 'yourGoal',
    titleKey: 'pages.onboarding.dreamScenario.title',
    type: 'statement',
  },
  {
    answers: goalsAnswers,
    path: 'goals-question',
    section: 'yourGoal',
    titleKey: 'pages.onboarding.goalsQuestion.title',
    type: 'single',
  },
  {
    answers: goalsTimelineAnswers,
    path: 'goals-timeline',
    section: 'yourGoal',
    subtitleKey: 'pages.onboarding.goalsTimeline.subtitle',
    titleKey: 'pages.onboarding.goalsTimeline.title',
    type: 'single',
  },
  {
    answers: firstMilestoneAnswers,
    path: 'first-milestone',
    section: 'yourGoal',
    subtitleKey: 'pages.onboarding.firstMilestone.subtitle',
    titleKey: 'pages.onboarding.firstMilestone.title',
    type: 'single',
  },
  {
    answers: timeAnswers,
    path: 'time-question',
    section: 'yourGoal',
    subtitleKey: 'pages.onboarding.timeQuestion.subtitle',
    titleKey: 'pages.onboarding.timeQuestion.title',
    type: 'single',
  },
  {
    emoji: '🧐',
    path: 'habits-feedback',
    section: 'yourGoal',
    titleKey: 'pages.onboarding.habitsFeedback.title',
    type: 'statement',
  },
  {
    answers: mistakesAnswers,
    path: 'mistakes-question',
    section: 'yourObstacles',
    subtitleKey: 'pages.onboarding.mistakesQuestion.subtitle',
    titleKey: 'pages.onboarding.mistakesQuestion.title',
    type: 'single',
  },
  {
    answers: selfEfficacyAnswers,
    path: 'self-efficacy',
    section: 'yourObstacles',
    subtitleKey: 'pages.onboarding.selfEfficacy.subtitle',
    titleKey: 'pages.onboarding.selfEfficacy.title',
    type: 'single',
  },
  {
    emoji: '🤝',
    path: 'obstacles-acknowledge',
    section: 'yourObstacles',
    titleKey: 'pages.onboarding.obstaclesAcknowledge.title',
    type: 'statement',
  },
  {
    answers: topicsAnswers,
    path: 'topics-question',
    section: 'yourObstacles',
    subtitleKey: 'pages.onboarding.topicsQuestion.subtitle',
    titleKey: 'pages.onboarding.topicsQuestion.title',
    type: 'multi',
  },
  {
    emoji: '💬',
    path: 'maria-testimonial',
    section: 'yourObstacles',
    titleKey: 'pages.onboarding.mariaTestimonial.title',
    type: 'statement',
  },
  {
    answers: cognitiveStyleAnswers,
    path: 'cognitive-style',
    section: 'yourObstacles',
    subtitleKey: 'pages.onboarding.cognitiveStyle.subtitle',
    titleKey: 'pages.onboarding.cognitiveStyle.title',
    type: 'single',
  },
  {
    answers: pastMethodsAnswers,
    path: 'past-methods',
    section: 'yourObstacles',
    subtitleKey: 'pages.onboarding.pastMethods.subtitle',
    titleKey: 'pages.onboarding.pastMethods.title',
    type: 'multi',
  },
  {
    answers: biggestFrustrationAnswers,
    path: 'biggest-frustration',
    section: 'yourObstacles',
    subtitleKey: 'pages.onboarding.biggestFrustration.subtitle',
    titleKey: 'pages.onboarding.biggestFrustration.title',
    type: 'single',
  },
  {
    emoji: '🙌',
    path: 'experience-feedback',
    section: 'yourObstacles',
    titleKey: 'pages.onboarding.experienceFeedback.title',
    type: 'statement',
  },
  {
    emoji: '📋',
    path: 'learning-profile-summary',
    section: 'yourPlan',
    titleKey: 'pages.onboarding.learningProfileSummary.title',
    type: 'statement',
  },
  {
    emoji: '✨',
    path: 'personalized-plan-preview',
    section: 'yourPlan',
    titleKey: 'pages.onboarding.personalizedPlanPreview.title',
    type: 'statement',
  },
  {
    answers: picturingAnswers,
    path: 'picturing-question',
    section: 'yourPlan',
    subtitleKey: 'pages.onboarding.picturingQuestion.subtitle',
    titleKey: 'pages.onboarding.picturingQuestion.title',
    type: 'single',
  },
  {
    emoji: '🔮',
    path: 'picturing-feedback',
    section: 'yourPlan',
    titleKey: 'pages.onboarding.picturingFeedback.title',
    type: 'statement',
  },
  {
    answers: identityShiftAnswers,
    path: 'identity-shift',
    section: 'yourPlan',
    subtitleKey: 'pages.onboarding.identityShift.subtitle',
    titleKey: 'pages.onboarding.identityShift.title',
    type: 'single',
  },
  {
    emoji: '💪',
    path: 'investment-reminder',
    section: 'yourPlan',
    titleKey: 'pages.onboarding.investmentReminder.title',
    type: 'statement',
  },
  {
    answers: finalCommitmentAnswers,
    path: 'final-commitment',
    section: 'yourPlan',
    subtitleKey: 'pages.onboarding.finalCommitment.subtitle',
    titleKey: 'pages.onboarding.finalCommitment.title',
    type: 'single',
  },
  {
    answers: subscriptionCountAnswers,
    path: 'subscription-count',
    section: 'yourPlan',
    titleKey: 'pages.onboarding.subscriptionCount.title',
    type: 'single',
  },
  // Last step, so the action redirects to /sign-up/company. Ten seconds
  // rather than the 1.5 it used to be: this is the last screen before the
  // account wall, and the wait is now carrying store ratings, reviews and two
  // questions instead of just passing time.
  {
    durationMs: 10000,
    path: 'crafting-progress',
    section: 'yourPlan',
    titleKey: 'pages.onboarding.craftingProgress.title',
    type: 'loader',
  },
]

/** How far through its own section a step sits, as a 0–1 fraction. */
export function getSectionProgress(step: FunnelStep): number {
  const sectionSteps = FUNNEL_STEPS.filter(
    (funnelStep) => funnelStep.section === step.section,
  )

  return (sectionSteps.indexOf(step) + 1) / sectionSteps.length
}
